import { FilterIcon, XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';

import type {
  AssignmentFilter,
  DateFilter,
  FilterState,
  TypeFilter,
} from './types';

interface Props {
  filters: FilterState;
  totalCount: number;
  onDateChange: (value: DateFilter) => void;
  onTypeChange: (value: TypeFilter) => void;
  onAssignmentChange: (value: AssignmentFilter) => void;
  onClear: () => void;
  /** Calendar view owns the date range itself, so it hides this select. */
  showDateFilter?: boolean;
  /** Tiers without employees have nothing to filter by assignment. */
  showAssignmentFilter?: boolean;
}

export function FilterBar({
  filters,
  totalCount,
  onDateChange,
  onTypeChange,
  onAssignmentChange,
  onClear,
  showDateFilter = true,
  showAssignmentFilter = true,
}: Props) {
  const { t } = useLanguage();

  const hasFilters =
    filters.date !== 'all' ||
    filters.type !== 'all' ||
    filters.assignment !== 'all';

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
        <FilterIcon className="w-3.5 h-3.5" />
        <span className="font-medium">{t.allJobs}</span>
      </div>

      {/* Date — maps to ?start_date_from/?start_date_to */}
      {showDateFilter && (
        <select
          className="px-3 py-1.5 text-xs border rounded-lg bg-background appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
          value={filters.date}
          onChange={(e) => onDateChange(e.target.value as DateFilter)}
          data-testid="filter-date"
        >
          <option value="all">{t.allDates}</option>
          <option value="today">{t.today}</option>
          <option value="week">{t.thisWeek}</option>
          <option value="month">{t.thisMonth}</option>
        </select>
      )}

      {/* Type — not a server query param; filtered client-side over the fetched page */}
      <select
        className="px-3 py-1.5 text-xs border rounded-lg bg-background appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
        value={filters.type}
        onChange={(e) => onTypeChange(e.target.value as TypeFilter)}
        data-testid="filter-type"
      >
        <option value="all">{t.allTypes}</option>
        <option value="recurring">{t.recurring}</option>
        <option value="one_time">{t.oneTime}</option>
      </select>

      {/* Assignment — client-side over the fetched page (assigned_employee_count) */}
      {showAssignmentFilter && (
        <select
          className="px-3 py-1.5 text-xs border rounded-lg bg-background appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
          value={filters.assignment}
          onChange={(e) => onAssignmentChange(e.target.value as AssignmentFilter)}
          data-testid="filter-assignment"
        >
          <option value="all">{t.assignmentAll}</option>
          <option value="unassigned">{t.assignmentUnassigned}</option>
          <option value="assigned">{t.assignmentAssigned}</option>
        </select>
      )}

      {hasFilters && (
        <button
          onClick={onClear}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="clear-filters"
        >
          <XIcon className="w-3.5 h-3.5" />
          {t.clearFilters}
        </button>
      )}

      <span className="ml-auto text-xs text-muted-foreground">
        {t.showingJobs} {totalCount} {t.jobsCount}
      </span>
    </div>
  );
}
